import { call, put, takeLatest, select, take, race } from "redux-saga/effects";
import delay from "@redux-saga/delay-p";
import actionTypes from "data/actions/actionTypes";
import {
    getUserResultAction,
    setLoginResultAction,
    loginRefreshAction,
} from "data/actions";
import { fetchUser } from "data/api";
import Constants from "data/constants";
import packageInfo from "../../../package.json";
import { signinRedirect, signout, getToken } from "data/loginFlow";
import { JL } from "data/logging";
import * as s from "data/reducers/selectors";
import { msToTime } from "data/utils";

const log = JL("userSaga");
const EXPIRATION_DELTA = 0.25; // percentage of unused token lifetime
const MAX_RACE_TIMEOUT = 2147483647; // max value for race effect

// worker Saga: will be fired on USER_FETCH_REQUESTED actions
function* getUser(action) {
    try {
        const user = yield call(fetchUser);
        window.CONFIG.web_version = packageInfo.version;
        window.CONFIG.provider_fileVersion = user.provider_fileVersion;
        window.CONFIG.provider_serviceVersion = user.provider_serviceVersion;
        window.CONFIG.general.features = user.features;
        log.info(() => ({
            msg: `Fetch user`,
            user,
            version: packageInfo.version,
        }));

        yield put(getUserResultAction({ status: Constants.OK, info: user }));
        //}
    } catch (error) {
        log.error(() => ({
            msg: `Error while getting user`,
            action,
            error,
        }));
        yield put(
            getUserResultAction({
                status: Constants.ERROR,
                message: error.title,
            })
        );
    }
}

function* logoutUser() {
    const idToken = yield select(s.loginIdTokenSelector);
    log.info(() => ({
        msg: `Logging out user`,
        idToken,
    }));
    yield call(signout, idToken);
}

function* refreshToken() {
    const refreshToken = yield select(s.loginRefreshTokenSelector);
    try {
        log.info(() => ({
            msg: `Renew Token with refresh_token`,
            grant_type: "refresh_token",
            client_id: window.CONFIG.auth.clientId,
            redirect_uri: window.CONFIG.auth.redirectUrl,
        }));

        const result = yield call(getToken, {
            client_id: window.CONFIG.auth.clientId,
            grant_type: "refresh_token",
            redirect_uri: window.CONFIG.auth.redirectUrl,
            refresh_token: refreshToken,
        });

        yield put(
            setLoginResultAction({
                status: Constants.OK,
                refreshToken: result.refresh_token,
                accessToken: result.access_token,
                idToken: result.id_token,
                expiresIn: result.expires_in,
                expiresOn: Date.now() + 1000 * result.expires_in,
            })
        );
    } catch (error) {
        log.error(() => ({
            msg: "Error while Token renewal with refresh_token",
            error,
        }));
        signinRedirect();
    }
}

function* setRenewal(action) {
    // Relative seconds, that are always set
    const expiresIn = (yield select(s.loginExpiresInSelector)) * 1000;
    // Absolute timestap that might be empty, therefor calculate it
    const expiresOn = parseInt(
        (yield select(s.loginExpiresOnSelector)) ??
            action.receivedAt + expiresIn,
        10
    );
    const expiresInMs = expiresOn - action.receivedAt;
    // Calculated delta, but not less than 1 minute
    const expiresInDelta = Math.max(60000, expiresIn * EXPIRATION_DELTA);
    // Calculated delay, but not less than 0
    const delayInMs = Math.max(0, expiresInMs - expiresInDelta);

    if (Number.isNaN(expiresIn) || Number.isNaN(action.receivedAt)) {
        log.error(() => ({
            msg: "Cannot calculate expiration delta, aborting setRenewal...",
            delayInMs,
            MAX_RACE_TIMEOUT,
            expiresOn,
            expiresIn,
            expiresInMs,
            expiresInDelta,
            receivedAt: action?.receivedAt,
            action,
        }));
        return;
    }
    /* istanbul ignore if */
    if (!window.CONFIG.auth.tokenEndpoint) {
        log.warn(() => ({
            msg: "OAuth Provider does not support hybrid grant flow, cannot renew token in background...",
            DEV: "IIdentityService.TokenEndpoint needs valid URL",
        }));
        return;
    }

    log.info(() => ({
        msg: `Token will be renewed in ${msToTime(
            Math.min(delayInMs, MAX_RACE_TIMEOUT)
        )}`,
        formated: {
            delayInMs: msToTime(delayInMs),
            MAX_RACE_TIMEOUT: msToTime(MAX_RACE_TIMEOUT),
            expiresOn: new Date(expiresOn),
            expiresIn: msToTime(expiresIn),
            expiresInMs: msToTime(expiresInMs),
            expiresInDelta: msToTime(expiresInDelta),
        },
        delayInMs,
        MAX_RACE_TIMEOUT,
        expiresOn,
        expiresIn,
        expiresInMs,
        expiresInDelta,
    }));

    const { timeout, ...actions } = yield race({
        login: take(actionTypes.LOGIN_RESULT),
        logout: take(actionTypes.LOGIN_SIGNOUT),
        renewal: take(actionTypes.LOGIN_SET_RENEWAL),
        timeout: delay(Math.min(delayInMs, MAX_RACE_TIMEOUT)),
    });

    if (timeout) {
        log.info(() => ({
            msg: "Token expiration delta reached, refreshing token...",
            delayInMs,
            MAX_RACE_TIMEOUT,
            expiresOn,
            expiresIn,
            expiresInMs,
            expiresInDelta,
        }));
        yield put(loginRefreshAction());
        return;
    }

    log.info(() => ({
        msg: "Aborting setRenewal...",
        actions,
    }));
}

function* userSaga() {
    yield takeLatest(actionTypes.USER_FETCH_REQUESTED, getUser);
    yield takeLatest(actionTypes.LOGIN_SIGNOUT, logoutUser);
    yield takeLatest(actionTypes.LOGIN_REFRESH, refreshToken);
    yield takeLatest(actionTypes.LOGIN_SET_RENEWAL, setRenewal);
}

export default userSaga;
